/**
 * Vehicle icon utilities for MapLibre.
 *
 * Loads SVG icons, converts them to white for SDF rendering,
 * and registers them with the MapLibre map instance.
 *
 * SDF (Signed Distance Field) icons allow dynamic coloring via
 * the `icon-color` paint property.
 *
 * Uses a layered defense for reliable icon loading:
 * 1. Bulk registration via registerVehicleIcons() on init and style reload
 * 2. On-demand recovery via styleimagemissing + loadIconOnDemand()
 * 3. Pre-decoded image cache for instant addImage() in the missing-image handler
 */

import type { Map as MapLibreMap } from 'maplibre-gl';
import type { TransportMode } from '../types/api';

// =============================================================================
// ICON CONFIGURATION
// =============================================================================

/** Map transport modes to SVG icon file names (without path/extension) */
export const TRANSPORT_MODE_ICONS: Record<TransportMode, string> = {
  bus: 'ic_bus',
  tram: 'ic_tram',
  train: 'ic_train',
  ferry: 'ic_boat',
  taxi: 'ic_taxi',
  teletaxi: 'ic_taxi', // fallback to taxi icon
};

/** Default icon for unknown transport modes */
export const DEFAULT_ICON = 'ic_bus';

/** Crosshair icon for V4/V5 position markers */
export const CROSSHAIR_ICON = 'ic_crosshair';

/** Icon size for rendering (source SVGs are 28x28) */
export const ICON_SIZE = 28;

/** All icon IDs that we manage — used to filter styleimagemissing events */
export const KNOWN_ICON_IDS: ReadonlySet<string> = new Set([
  ...new Set(Object.values(TRANSPORT_MODE_ICONS)),
  CROSSHAIR_ICON,
]);

// =============================================================================
// ICON HELPERS
// =============================================================================

/**
 * Get icon ID for a transport mode.
 *
 * @param mode - Transport mode (bus, tram, train, ferry, taxi, teletaxi)
 * @returns Icon ID string for use in MapLibre symbol layer
 */
export function getIconForMode(mode: TransportMode | string | undefined): string {
  if (!mode) return DEFAULT_ICON;
  return TRANSPORT_MODE_ICONS[mode as TransportMode] ?? DEFAULT_ICON;
}

// =============================================================================
// ICON REGISTRATION
// =============================================================================

/** Tracks whether bulk registration has completed */
let iconsRegistered = false;

/** Pre-decoded image cache — survives across mounts with reuseMaps */
const imageCache = new Map<string, HTMLImageElement>();

/** In-flight load promises for deduplication */
const loadingPromises = new Map<string, Promise<void>>();

/**
 * Load an SVG file and convert it to white for SDF rendering.
 *
 * @param svgPath - Path to SVG file
 * @returns Promise resolving to white SVG data URL
 */
async function loadAndWhitenSvg(svgPath: string): Promise<string> {
  const response = await fetch(svgPath);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${svgPath}: ${response.status}`);
  }
  let svgText = await response.text();

  // Replace any fill color with white for SDF
  // The original SVGs use fill="#3c4650"
  svgText = svgText.replace(/fill="[^"]*"/g, 'fill="#ffffff"');

  // Convert to data URL
  const encoded = encodeURIComponent(svgText);
  return `data:image/svg+xml,${encoded}`;
}

/**
 * Create an HTMLImageElement from a data URL.
 *
 * @param dataUrl - Image data URL
 * @param width - Image width
 * @param height - Image height
 * @returns Promise resolving to loaded HTMLImageElement
 */
function loadImage(dataUrl: string, width: number, height: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image(width, height);
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * Load a single icon on demand (for styleimagemissing handler).
 *
 * Uses the pre-decoded image cache for instant addImage() when available.
 * Deduplicates concurrent loads for the same icon ID.
 *
 * @param map - MapLibre map instance
 * @param iconId - Icon ID to load
 */
export async function loadIconOnDemand(map: MapLibreMap, iconId: string): Promise<void> {
  if (!KNOWN_ICON_IDS.has(iconId)) return;
  if (map.hasImage(iconId)) return;

  // Try pre-decoded cache first (instant, synchronous addImage)
  const cached = imageCache.get(iconId);
  if (cached) {
    try {
      map.addImage(iconId, cached, { sdf: true });
    } catch {
      // Already added by another path (TOCTOU with hasImage)
    }
    return;
  }

  // Deduplicate concurrent loads for the same icon
  if (loadingPromises.has(iconId)) {
    await loadingPromises.get(iconId);
    // After the shared load completes, add from cache if not yet on map
    if (!map.hasImage(iconId) && imageCache.has(iconId)) {
      try {
        map.addImage(iconId, imageCache.get(iconId)!, { sdf: true });
      } catch {
        // Race with another addImage call
      }
    }
    return;
  }

  const promise = (async () => {
    const svgPath = `/vehicle_icons/svg/${iconId}.svg`;
    const dataUrl = await loadAndWhitenSvg(svgPath);
    const img = await loadImage(dataUrl, ICON_SIZE, ICON_SIZE);
    imageCache.set(iconId, img);
    if (!map.hasImage(iconId)) {
      map.addImage(iconId, img, { sdf: true });
    }
  })();

  loadingPromises.set(iconId, promise);
  try {
    await promise;
  } catch (error) {
    console.warn(`Failed to load vehicle icon on demand: ${iconId}`, error);
  } finally {
    loadingPromises.delete(iconId);
  }
}

/**
 * Register all vehicle icons with a MapLibre map instance.
 *
 * Icons are registered as SDF (Signed Distance Field) images, allowing
 * dynamic coloring via the `icon-color` paint property. Also populates
 * the image cache for instant recovery via styleimagemissing.
 *
 * @param map - MapLibre map instance
 */
export async function registerVehicleIcons(map: MapLibreMap): Promise<void> {
  if (iconsRegistered) return;

  const iconIds = [...KNOWN_ICON_IDS];

  const registrations = iconIds.map(async (iconId) => {
    if (map.hasImage(iconId)) return;

    try {
      const svgPath = `/vehicle_icons/svg/${iconId}.svg`;
      const dataUrl = await loadAndWhitenSvg(svgPath);
      const img = await loadImage(dataUrl, ICON_SIZE, ICON_SIZE);

      // Populate cache for styleimagemissing recovery
      imageCache.set(iconId, img);

      if (!map.hasImage(iconId)) {
        map.addImage(iconId, img, { sdf: true });
      }
    } catch (error) {
      console.warn(`Failed to load vehicle icon: ${iconId}`, error);
    }
  });

  await Promise.all(registrations);
  iconsRegistered = true;
}

/**
 * Reset icon registration state (for style reload/HMR).
 */
export function resetIconRegistration(): void {
  iconsRegistered = false;
}
